import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { FileText, ArrowLeft, ArrowRight, Save, Download, Plus, Trash2, Eye, X, Lock } from 'lucide-react';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import ResumeTemplate from '../components/ResumeTemplate';
import PaymentModal from '../components/PaymentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Logo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResumeBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken, user } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPresets, setShowPresets] = useState(true);
  const [showBasicColors, setShowBasicColors] = useState(false);
  const [showImageSettings, setShowImageSettings] = useState(false);

  // Check for auto-open payment from navigation (save & pay flow)
  useEffect(() => {
    if (location.state?.openPayment) {
      setShowPayment(true);
      // Clear state to prevent reopening on generic re-renders, 
      // though react-router state persists until next nav. 
      // We can just rely on the modal close logic.
      window.history.replaceState({}, document.title)
    }
  }, [location.state]);

  const [resumeData, setResumeData] = useState({
    title: 'My Resume',
    personal_info: {
      full_name: '',
      email: '',
      phone: '',
      address: '',
      linkedin: '',
      website: '',
      summary: ''
    },
    education: [],
    work_experience: [],
    skills: [],
    extras: {},
    template_id: 'template1',
    colors: {
      titleColor: '#1e40af',
      textColor: '#4b5563',
      backgroundColor: '#ffffff',
      sidebarTitleColor: '#1e40af',
      sidebarTextColor: '#333333',
      sidebarBackgroundColor: '#f3f4f6'
    },
    customization: {
      imageSize: 110,
      imageShape: 'circle' // 'square', 'circle', 'rounded'
    }
  });

  const [imageLoading, setImageLoading] = useState(true);
  const [imageKey, setImageKey] = useState(Date.now());
  const [profileImageBase64, setProfileImageBase64] = useState(null);

  // Effect to handle profile image preloading with cache busting
  useEffect(() => {
    if (user?.picture) {
      setImageLoading(true);
      const img = new Image();
      // Add a timestamp to bypass browser cache checking and force a verification
      img.src = `${user.picture}?t=${Date.now()}`;

      const handleLoad = () => {
        // Image is valid and loaded in browser cache
        setProfileImageBase64(null); // Clear base64 if any
        setImageLoading(false);
        setImageKey(Date.now());
      };

      img.onload = handleLoad;
      img.onerror = () => {
        console.warn('Profile image failed to load');
        setImageLoading(false);
      };

      // Cleanup
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    } else {
      setImageLoading(false);
    }
  }, [user?.picture]);

  const [skillsInputText, setSkillsInputText] = useState(resumeData.skills.join(', '));



  useEffect(() => {
    fetchTemplates();
    if (id && id !== 'new') {
      fetchResume();
      checkPaymentStatus();
    }

  }, [id]);

  useEffect(() => {
    // This synchronizes the local input text when resumeData.skills changes (e.g., after initial fetch)
    setSkillsInputText(resumeData.skills.join(', '));
  }, [resumeData.skills]);




  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const fetchResume = async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(`${API}/resumes/${id}`, {
        headers,
        withCredentials: true
      });
      const fetchedData = response.data;

      // Legacy support: check if they exist on root (if schema changes later), otherwise check in extras
      const loadedColors = fetchedData.colors || fetchedData.extras?.colors || {};
      const loadedCustomization = fetchedData.customization || fetchedData.extras?.customization || {};

      setResumeData(prev => ({
        ...prev,
        ...fetchedData,
        // Deep merge colors to preserve defaults
        colors: {
          ...prev.colors,
          ...loadedColors
        },
        // Deep merge customization
        customization: {
          ...prev.customization,
          ...loadedCustomization
        }
      }));
    } catch (error) {
      toast.error('Failed to load resume');
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(`${API}/payments/check/${id}`, {
        headers,
        withCredentials: true
      });
      setCanDownload(response.data.can_download);
    } catch (error) {
      console.error('Failed to check payment');
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section === 'root') {
      setResumeData({ ...resumeData, [field]: value });
    } else {
      setResumeData({
        ...resumeData,
        [section]: { ...resumeData[section], [field]: value }
      });
    }
  };

  const handleSkillsChange = (e) => {
    setSkillsInputText(e.target.value);
  };

  // NEW HANDLER: Parses and saves to main resumeData only when the Textarea loses focus
  const handleSkillsBlur = () => {
    const skills = skillsInputText.split(',')
      .map(s => s.trim())
      .filter(s => s); // Removes empty strings

    setResumeData({ ...resumeData, skills });
  };

  const handleArrayAdd = (section, item) => {
    setResumeData({
      ...resumeData,
      [section]: [...resumeData[section], item]
    });
  };

  const handleArrayUpdate = (section, index, field, value) => {
    const updated = [...resumeData[section]];
    updated[index] = { ...updated[index], [field]: value };
    setResumeData({ ...resumeData, [section]: updated });
  };

  const handleArrayRemove = (section, index) => {
    setShowPreview(false);
    setResumeData({
      ...resumeData,
      [section]: resumeData[section].filter((_, i) => i !== index)
    });
    setTimeout(() => {
      setShowPreview(true);
    }, 100)
  };

  const handleSave = async (andPay = false) => {
    setLoading(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      // Prepare payload: pack colors and customization into extras for persistence without schema change
      const payload = {
        ...resumeData,
        extras: {
          ...resumeData.extras,
          colors: resumeData.colors,
          customization: resumeData.customization
        }
      };

      if (id && id !== 'new') {
        await axios.put(`${API}/resumes/${id}`, payload, {
          headers,
          withCredentials: true
        });
        toast.success('Resume updated successfully');
        if (andPay) {
          setShowPayment(true);
        }
      } else {
        const response = await axios.post(`${API}/resumes`, payload, {
          headers,
          withCredentials: true
        });
        toast.success('Resume created successfully');
        // Navigate to the new ID, and pass state to open payment modal if requested
        navigate(`/resume/${response.data.id}`, { state: { openPayment: andPay } });
      }
    } catch (error) {
      toast.error('Failed to save resume');
    } finally {
      setLoading(false);
    }
  };
  // Robust image resolver using Fetch to Get Blob -> Base64
  // This bypasses Tainted Canvas issues by fetching the data directly
  const resolveImageToDataURL = async (url) => {
    if (!url) return url;

    // Ensure absolute URL for consistency
    let targetUrl = url;
    if (targetUrl.startsWith('/') && !targetUrl.startsWith('http')) {
      targetUrl = `${window.location.origin}${targetUrl}`;
    }

    // Add cache buster
    if (targetUrl.includes('?')) {
      targetUrl += `&bust=${Date.now()}`;
    } else {
      targetUrl += `?bust=${Date.now()}`;
    }

    try {
      const response = await fetch(targetUrl, { credentials: 'include' });

      // Check for valid response
      if (!response.ok) {
        console.warn(`Image fetch failed: ${response.status} ${response.statusText}`);
        // Fallback: If /uploads fails, try /api/uploads (for backend proxy)
        if (targetUrl.includes('/uploads/')) {
          const fallbackUrl = targetUrl.replace('/uploads/', '/api/uploads/');
          console.log(`Retrying with fallback: ${fallbackUrl}`);
          const fbResponse = await fetch(fallbackUrl, { credentials: 'include' });
          if (fbResponse.ok) {
            const fbBlob = await fbResponse.blob();
            return await blobToBase64(fbBlob);
          }
        }
        return url; // Give up, return original
      }

      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) {
        console.warn(`Invalid image type: ${blob.type}`);
        return url;
      }

      return await blobToBase64(blob);

    } catch (e) {
      console.warn('Image fetch error:', e);
      return url;
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleDownload = async (forcePaid = false) => {
    // Determine the paid status: check override first, then state
    const isPaid = forcePaid === true || canDownload;

    const toastId = toast.loading("Preparing download...");
    try {
      // Resolve image to Base64 to avoid CORS/Fetch issues in react-pdf
      // Match the URL construction logic from PDFViewer to ensure consistency
      const targetImage = user?.picture
        ? (user.picture.startsWith('/uploads')
          ? `${BACKEND_URL}${user.picture}?t=${imageKey}`
          : `${user.picture}?t=${imageKey}`)
        : resumeData.personal_info.profile_picture;
      let finalProfilePicture = targetImage;

      if (targetImage) {
        finalProfilePicture = await resolveImageToDataURL(targetImage);
      }

      const blob = await pdf(
        <ResumeTemplate
          isPaid={isPaid}
          data={{
            ...resumeData,
            personal_info: {
              ...resumeData.personal_info,
              profile_picture: finalProfilePicture
            }
          }}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resumeData.title || 'resume'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.dismiss(toastId);
      toast.success("Download started!");
    } catch (e) {
      console.error("Download failed:", e);
      toast.error("Download failed. Please try again.", { id: toastId });
    }
  };

  const handlePaymentSuccess = async () => {
    setCanDownload(true);
    setShowPayment(false);
    toast.success('Payment successful!');
    handleDownload(true); // Trigger auto-download with explicit paid status
  };

  const steps = [
    { number: 1, title: 'Personal Info', icon: '👤' },
    { number: 2, title: 'Education', icon: '🎓' },
    { number: 3, title: 'Work Experience', icon: '💼' },
    { number: 4, title: 'Skills', icon: '⚡' },
    { number: 5, title: 'Template', icon: '🎨' }
  ];

  if (imageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
              className="px-2 sm:px-4"
            >
              <ArrowLeft className="h-5 w-5 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center space-x-2">
              <div className="p-1 rounded-lg bg-blue-600">
                <Logo className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-blue-600 hidden sm:block">Resume Builder</span>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              data-testid="toggle-preview-btn"
              className="px-2 sm:px-4"
            >
              <Eye className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{showPreview ? 'Hide' : 'Show'} Preview</span>
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4"
              data-testid="save-resume-btn"
            >
              <Save className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Save</span>
            </Button>
            {id && id !== 'new' && (
              <Button
                onClick={() => canDownload ? handleDownload() : setShowPayment(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4"
                data-testid="download-resume-btn"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Download</span>
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="space-y-6">
            {/* Progress Steps */}
            <Card className="p-4 sm:p-6">
              <div className="flex items-center justify-center md:justify-between gap-4 md:gap-0 mb-6">
                {steps.map((step, index) => (
                  <React.Fragment key={step.number}>
                    <div
                      className={`flex flex-col items-center cursor-pointer ${currentStep === step.number ? 'text-blue-600' : 'text-gray-400'
                        }`}
                      onClick={() => setCurrentStep(step.number)}
                    >
                      <div
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm md:text-xl font-bold mb-1 md:mb-2 transition-all ${currentStep === step.number
                          ? 'bg-blue-600 text-white scale-105 md:scale-110'
                          : 'bg-gray-200 text-gray-500'
                          }`}
                      >
                        {step.number}
                      </div>
                      <span className="hidden md:block text-xs font-medium text-center">{step.title}</span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="hidden md:block flex-1 h-1 bg-gray-200 mx-2"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Card>

            {/* Form Content */}
            <Card className="p-4 sm:p-6">
              {/* Step 1: Personal Info */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold mb-4">Personal Information</h2>
                  <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mb-4 text-sm text-blue-800 flex items-center">
                    <span className="mr-2">ℹ️</span>
                    Your profile picture will be automatically used for your resume. To add one, please upload it in your dashboard; otherwise, your initials will be displayed.
                  </div>
                  <div>
                    <Label>Resume Title</Label>
                    <Input
                      value={resumeData.title}
                      onChange={(e) => handleInputChange('root', 'title', e.target.value)}
                      placeholder="e.g., Software Engineer Resume"
                      data-testid="resume-title-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={resumeData.personal_info.full_name}
                        onChange={(e) => handleInputChange('personal_info', 'full_name', e.target.value)}
                        data-testid="full-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={resumeData.personal_info.email}
                        onChange={(e) => handleInputChange('personal_info', 'email', e.target.value)}
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={resumeData.personal_info.phone}
                        onChange={(e) => handleInputChange('personal_info', 'phone', e.target.value)}
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={resumeData.personal_info.address}
                        onChange={(e) => handleInputChange('personal_info', 'address', e.target.value)}
                        data-testid="address-input"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>LinkedIn (optional)</Label>
                      <Input
                        value={resumeData.personal_info.linkedin}
                        onChange={(e) => handleInputChange('personal_info', 'linkedin', e.target.value)}
                        data-testid="linkedin-input"
                      />
                    </div>
                    <div>
                      <Label>Website (optional)</Label>
                      <Input
                        value={resumeData.personal_info.website}
                        onChange={(e) => handleInputChange('personal_info', 'website', e.target.value)}
                        data-testid="website-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Professional Summary</Label>
                    <div className="relative">
                      <Textarea
                        value={resumeData.personal_info.summary}
                        onChange={(e) => handleInputChange('personal_info', 'summary', e.target.value)}
                        rows={6}
                        placeholder="Brief overview of your professional background and goals"
                        data-testid="summary-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute right-2 bottom-2 text-xs bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                        onClick={async () => {
                          const currentSummary = resumeData.personal_info.summary;
                          const jobTitle = resumeData.title;

                          if (!currentSummary && !jobTitle) {
                            toast.error("Please enter a Resume Title or a draft summary first.");
                            return;
                          }
                          const toastId = toast.loading("Generating Summary...");
                          try {
                            const promptText = currentSummary && currentSummary.length > 10
                              ? `Rewrite and improve the following professional summary for a professional resume. Make it impactful, concise (2-3 sentences), and highlight key strengths. Output ONLY the text. Original: "${currentSummary}"`
                              : `Generate a professional resume summary (2-3 sentences) for a "${jobTitle || 'Professional'}". Focus on general expertise, adaptability, and career goals. Output ONLY the summary text.`;

                            const res = await axios.post(`${API}/ai/generate`, { prompt: promptText }, {
                              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
                              withCredentials: true
                            });

                            if (res.data.text) {
                              handleInputChange('personal_info', 'summary', res.data.text);
                              toast.success("Generated successfully!", { id: toastId });
                            }
                          } catch (e) {
                            toast.error("AI generation failed.", { id: toastId });
                          }
                        }}
                      >
                        ✨ Enhance with AI
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Education */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold">Education</h2>
                    <Button
                      onClick={() =>
                        handleArrayAdd('education', {
                          institution: '',
                          degree: '',
                          field_of_study: '',
                          start_date: '',
                          end_date: '',
                          description: ''
                        })
                      }
                      size="sm"
                      data-testid="add-education-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Education
                    </Button>
                  </div>

                  {resumeData.education.map((edu, index) => (
                    <Card key={index} className="p-4 border-2">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold">Education {index + 1}</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleArrayRemove('education', index)}
                          className="text-red-600"
                          data-testid={`remove-education-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <Input
                          placeholder="Institution"
                          value={edu.institution}
                          onChange={(e) => handleArrayUpdate('education', index, 'institution', e.target.value)}
                          data-testid={`education-institution-${index}`}
                        />
                        <div className="grid md:grid-cols-2 gap-3">
                          <Input
                            placeholder="Degree"
                            value={edu.degree}
                            onChange={(e) => handleArrayUpdate('education', index, 'degree', e.target.value)}
                            data-testid={`education-degree-${index}`}
                          />
                          <Input
                            placeholder="Field of Study"
                            value={edu.field_of_study}
                            onChange={(e) => handleArrayUpdate('education', index, 'field_of_study', e.target.value)}
                            data-testid={`education-field-${index}`}
                          />
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <Input
                            type="month"
                            placeholder="Start Date"
                            value={edu.start_date}
                            onChange={(e) => handleArrayUpdate('education', index, 'start_date', e.target.value)}
                            data-testid={`education-start-${index}`}
                          />
                          <Input
                            type="month"
                            placeholder="End Date"
                            value={edu.end_date}
                            onChange={(e) => handleArrayUpdate('education', index, 'end_date', e.target.value)}
                            data-testid={`education-end-${index}`}
                          />
                        </div>
                        <div className="relative">
                          <Textarea
                            placeholder="Description (optional)"
                            value={edu.description}
                            onChange={(e) => handleArrayUpdate('education', index, 'description', e.target.value)}
                            rows={6}
                            data-testid={`education-desc-${index}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="absolute right-2 bottom-2 text-xs bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                            onClick={async () => {
                              const degree = edu.degree;
                              const field = edu.field_of_study;

                              if (!degree && !field && (!edu.description || edu.description.length < 5)) {
                                toast.error("Please enter a Degree or Field of Study first.");
                                return;
                              }
                              const toastId = toast.loading("Generating description...");
                              try {
                                const promptText = edu.description && edu.description.length > 5
                                  ? `Rewrite and improve the following education description for a resume. Focus on coursework, projects, or honors. Output ONLY the text. Original: "${edu.description}"`
                                  : `Generate a concise description (1-2 sentences) of typical coursework and skills gained for a "${degree || 'Degree'}" in "${field || 'this field'}". Output ONLY the text.`;

                                const res = await axios.post(`${API}/ai/generate`, { prompt: promptText }, {
                                  headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
                                  withCredentials: true
                                });

                                if (res.data.text) {
                                  handleArrayUpdate('education', index, 'description', res.data.text);
                                  toast.success("Generated successfully!", { id: toastId });
                                }
                              } catch (e) {
                                toast.error("AI generation failed.", { id: toastId });
                              }
                            }}
                          >
                            ✨ Enhance
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Step 3: Work Experience */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold">Work Experience</h2>
                    <Button
                      onClick={() =>
                        handleArrayAdd('work_experience', {
                          company: '',
                          position: '',
                          start_date: '',
                          end_date: '',
                          description: '',
                          current: false
                        })
                      }
                      size="sm"
                      data-testid="add-experience-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Experience
                    </Button>
                  </div>

                  {resumeData.work_experience.map((exp, index) => (
                    <Card key={index} className="p-4 border-2">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold">Experience {index + 1}</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleArrayRemove('work_experience', index)}
                          className="text-red-600"
                          data-testid={`remove-experience-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                          <Input
                            placeholder="Company"
                            value={exp.company}
                            onChange={(e) => handleArrayUpdate('work_experience', index, 'company', e.target.value)}
                            data-testid={`experience-company-${index}`}
                          />
                          <Input
                            placeholder="Position"
                            value={exp.position}
                            onChange={(e) => handleArrayUpdate('work_experience', index, 'position', e.target.value)}
                            data-testid={`experience-position-${index}`}
                          />
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <Input
                            type="month"
                            placeholder="Start Date"
                            value={exp.start_date}
                            onChange={(e) => handleArrayUpdate('work_experience', index, 'start_date', e.target.value)}
                            data-testid={`experience-start-${index}`}
                          />
                          <Input
                            type="month"
                            placeholder="End Date"
                            value={exp.end_date}
                            onChange={(e) => handleArrayUpdate('work_experience', index, 'end_date', e.target.value)}
                            disabled={exp.current}
                            data-testid={`experience-end-${index}`}
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={exp.current}
                            onChange={(e) => handleArrayUpdate('work_experience', index, 'current', e.target.checked)}
                            className="rounded"
                            data-testid={`experience-current-${index}`}
                          />
                          <Label>Currently working here</Label>
                        </div>
                        <div className="relative">
                          <Textarea
                            placeholder="Job description and achievements"
                            value={exp.description}
                            onChange={(e) => handleArrayUpdate('work_experience', index, 'description', e.target.value)}
                            rows={6}
                            data-testid={`experience-desc-${index}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="absolute right-2 bottom-2 text-xs bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                            onClick={async () => {
                              if (!exp.position && (!exp.description || exp.description.length < 3)) {
                                toast.error("Please enter a 'Position' title so AI knows what to generate (or type a draft description).");
                                return;
                              }
                              const toastId = toast.loading("Enhancing with AI...");
                              try {
                                const promptText = exp.description && exp.description.length > 5
                                  ? `Rewrite and EXPAND the following job description for the role of "${exp.position || 'Professional'}" to be professional, impactful, and detailed. Generate 3-5 strong, result-oriented bullet points. Separate each bullet point with TWO new lines (\n\n). Output ONLY the content, no conversational filler. Original text: "${exp.description}"`
                                  : `Generate 3-5 professional, result-oriented resume bullet points for the role of "${exp.position}". Focus on typical high-impact responsibilities and achievements for this role. Separate each bullet point with TWO new lines (\n\n). Output ONLY the content, no conversational filler.`;

                                const res = await axios.post(`${API}/ai/generate`, {
                                  prompt: promptText
                                }, {
                                  headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
                                  withCredentials: true
                                });

                                if (res.data.text) {
                                  handleArrayUpdate('work_experience', index, 'description', res.data.text);
                                  toast.success("Enhanced successfully!", { id: toastId });
                                }
                              } catch (e) {
                                toast.error("AI enhancement failed. Try again.", { id: toastId });
                              }
                            }}
                          >
                            ✨ Enhance with AI
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Step 4: Skills */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold mb-4">Skills</h2>
                  <div>
                    <Label>Add your skills (comma-separated)</Label>
                    <div className="relative">
                      <Textarea
                        value={skillsInputText}
                        onChange={handleSkillsChange}
                        onBlur={handleSkillsBlur}
                        rows={6}
                        placeholder="e.g., JavaScript, React, Python, Project Management"
                        data-testid="skills-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute right-2 bottom-2 text-xs bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                        onClick={async () => {
                          const positions = resumeData.work_experience.map(e => e.position).filter(Boolean).join(", ");
                          const targetRole = resumeData.title || positions;

                          if (!targetRole && skillsInputText.length < 3) {
                            toast.error("Please add Work Experience positions or a Resume Title first.");
                            return;
                          }

                          const toastId = toast.loading("Generating skills...");
                          try {
                            const promptText = `Generate a comma-separated list of 10-15 relevant hard and soft skills for a "${targetRole}". Output ONLY the comma-separated list, no formatting labels.`;

                            const res = await axios.post(`${API}/ai/generate`, { prompt: promptText }, {
                              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
                              withCredentials: true
                            });

                            if (res.data.text) {
                              // Append to existing or replace if empty
                              const newSkills = res.data.text.replace(/\n/g, " ").trim();
                              setSkillsInputText(prev => prev ? `${prev}, ${newSkills}` : newSkills);
                              // Trigger parsing immediately or wait for blur? 
                              // We update the text, user can review.
                              toast.success("Skills generated!", { id: toastId });
                            }
                          } catch (e) {
                            toast.error("AI generation failed.", { id: toastId });
                          }
                        }}
                      >
                        ✨ Suggest Skills
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {resumeData.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 5: Template Selection */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-4">Choose Design & Colors</h2>
                    <p className="text-gray-600 mb-6">Select a template and customize the color theme to match your personal brand.</p>

                    {/* Color Picker Section */}
                    <Card className="p-6 mb-8 border-2 border-indigo-50 bg-indigo-50/30">
                      {/* Preset Palettes (Now at Top) */}
                      <div className="mb-2">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                          <label className="block text-sm font-bold text-gray-900 uppercase tracking-wide">Quick Color Presets</label>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 h-8"
                              onClick={async () => {
                                if (!resumeData.title) {
                                  toast.error("Please enter a Resume Title first (Step 1).");
                                  return;
                                }
                                const toastId = toast.loading("Designing color theme...");
                                try {
                                  const promptText = `Suggest a professional, harmonious color palette for a resume of a "${resumeData.title}". Return a valid JSON object with these exact keys: "titleColor", "textColor", "sidebarTitleColor", "sidebarTextColor", "backgroundColor", "sidebarBackgroundColor". Use hex codes. Do not include markdown code blocks.`;

                                  const res = await axios.post(`${API}/ai/generate`, { prompt: promptText }, {
                                    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
                                    withCredentials: true
                                  });

                                  if (res.data.text) {
                                    // Cleanup likely markdown wrap
                                    const cleanJson = res.data.text.replace(/```json/g, '').replace(/```/g, '').trim();
                                    const colors = JSON.parse(cleanJson);

                                    setResumeData(prev => ({
                                      ...prev,
                                      colors: { ...prev.colors, ...colors }
                                    }));
                                    toast.success("Theme applied!", { id: toastId });
                                  }
                                } catch (e) {
                                  console.error(e);
                                  toast.error("Failed to generate theme.", { id: toastId });
                                }
                              }}
                            >
                              <span className="hidden sm:inline">✨ Generate Theme Colors with AI</span>
                              <span className="sm:hidden">✨ AI Theme</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowPresets(!showPresets)}
                              className="text-xs text-blue-600 hover:text-blue-700 h-8"
                            >
                              {showPresets ? 'Collapse' : 'Expand'}
                            </Button>
                          </div>
                        </div>

                        {showPresets && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            {[
                              { name: 'Classic Blue', p: '#1e40af', s: '#4b5563' },
                              { name: 'Emerald', p: '#059669', s: '#374151' },
                              { name: 'Royal Purple', p: '#7c3aed', s: '#4b5563' },
                              { name: 'Crimson', p: '#dc2626', s: '#1f2937' },
                              { name: 'Charcoal', p: '#111827', s: '#4b5563' },
                              { name: 'Ocean', p: '#0891b2', s: '#155e75' },
                              { name: 'Gold', p: '#d97706', s: '#78350f' },
                            ].map((preset) => (
                              <button
                                key={preset.name}
                                onClick={() => setResumeData({
                                  ...resumeData,
                                  colors: {
                                    ...resumeData.colors,
                                    titleColor: preset.p,
                                    textColor: preset.s,
                                    sidebarTitleColor: preset.p,
                                    sidebarTextColor: '#333333',
                                    backgroundColor: '#ffffff',
                                    sidebarBackgroundColor: '#f3f4f6'
                                  }
                                })}
                                className="flex items-center justify-center space-x-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-blue-400 hover:shadow-md transition-all text-xs font-semibold text-gray-700 shadow-sm"
                                title={`Apply ${preset.name} Theme`}
                              >
                                <div className="flex -space-x-1">
                                  <div className="w-4 h-4 rounded-full ring-2 ring-white" style={{ backgroundColor: preset.p }}></div>
                                  <div className="w-4 h-4 rounded-full ring-2 ring-white" style={{ backgroundColor: preset.s }}></div>
                                </div>
                                <span className="truncate">{preset.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Basic Colors Customization (Collapsible) */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowBasicColors(!showBasicColors)}
                          className="w-full justify-between bg-white hover:bg-gray-50 text-gray-700 font-medium h-auto whitespace-normal text-left py-3"
                        >
                          <div className="flex items-center">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 mr-2 shadow-sm shrink-0"></div>
                            <span>Basic Colors Customization</span>
                          </div>
                          {showBasicColors ? <Eye className="w-4 h-4 text-blue-600" /> : <Plus className="w-4 h-4" />}
                        </Button>

                        {showBasicColors && (
                          <div className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-4 duration-300">
                            {/* Title Color */}
                            <div className="flex flex-wrap gap-2 items-center justify-between p-3 rounded-md border border-gray-200 bg-white shadow-sm hover:border-blue-300 transition-colors">
                              <label className="text-sm font-medium text-gray-700">Title Color</label>
                              <div className="flex items-center space-x-3 shrink-0">
                                <div className="relative w-8 h-8 rounded-full shadow-sm ring-1 ring-black/10 overflow-hidden group hover:ring-blue-400 transition-all cursor-pointer">
                                  <input
                                    type="color"
                                    value={resumeData.colors?.titleColor || '#1e40af'}
                                    onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, titleColor: e.target.value } })}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  />
                                  <div className="w-full h-full" style={{ backgroundColor: resumeData.colors?.titleColor || '#1e40af' }}></div>
                                </div>
                                <input
                                  type="text"
                                  value={resumeData.colors?.titleColor || '#1e40af'}
                                  onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, titleColor: e.target.value } })}
                                  className="w-20 h-8 px-2 text-xs font-mono border border-gray-200 rounded uppercase text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </div>

                            {/* Text Color */}
                            <div className="flex flex-wrap gap-2 items-center justify-between p-3 rounded-md border border-gray-200 bg-white shadow-sm hover:border-blue-300 transition-colors">
                              <label className="text-sm font-medium text-gray-700">Text Color</label>
                              <div className="flex items-center space-x-3 shrink-0">
                                <div className="relative w-8 h-8 rounded-full shadow-sm ring-1 ring-black/10 overflow-hidden group hover:ring-blue-400 transition-all cursor-pointer">
                                  <input
                                    type="color"
                                    value={resumeData.colors?.textColor || '#333333'}
                                    onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, textColor: e.target.value } })}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  />
                                  <div className="w-full h-full" style={{ backgroundColor: resumeData.colors?.textColor || '#333333' }}></div>
                                </div>
                                <input
                                  type="text"
                                  value={resumeData.colors?.textColor || '#333333'}
                                  onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, textColor: e.target.value } })}
                                  className="w-20 h-8 px-2 text-xs font-mono border border-gray-200 rounded uppercase text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </div>

                            {/* Background Color */}
                            <div className="flex flex-wrap gap-2 items-center justify-between p-3 rounded-md border border-gray-200 bg-white shadow-sm hover:border-blue-300 transition-colors">
                              <label className="text-sm font-medium text-gray-700">Background Color</label>
                              <div className="flex items-center space-x-3 shrink-0">
                                <div className="relative w-8 h-8 rounded-full shadow-sm ring-1 ring-black/10 overflow-hidden group hover:ring-blue-400 transition-all cursor-pointer">
                                  <input
                                    type="color"
                                    value={resumeData.colors?.backgroundColor || '#ffffff'}
                                    onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, backgroundColor: e.target.value } })}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  />
                                  <div className="w-full h-full" style={{ backgroundColor: resumeData.colors?.backgroundColor || '#ffffff' }}></div>
                                </div>
                                <input
                                  type="text"
                                  value={resumeData.colors?.backgroundColor || '#ffffff'}
                                  onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, backgroundColor: e.target.value } })}
                                  className="w-20 h-8 px-2 text-xs font-mono border border-gray-200 rounded uppercase text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Image Customization (Collapsible - Moved Here) */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowImageSettings(!showImageSettings)}
                          className="w-full justify-between bg-white hover:bg-gray-50 text-gray-700 font-medium h-auto whitespace-normal text-left py-3"
                        >
                          <span>Image Customization</span>
                          {showImageSettings ? <Eye className="w-4 h-4 text-blue-600 shrink-0 ml-2" /> : <Plus className="w-4 h-4 shrink-0 ml-2" />}
                        </Button>

                        {showImageSettings && (
                          <div className="mt-4 grid md:grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
                            <div>
                              <Label className="mb-2 block text-sm font-medium text-gray-700">Image Size: {resumeData.customization?.imageSize || 110}px</Label>
                              <input
                                type="range"
                                min="40"
                                max="150"
                                value={resumeData.customization?.imageSize || 110}
                                onChange={(e) => setResumeData({
                                  ...resumeData,
                                  customization: { ...resumeData.customization, imageSize: parseInt(e.target.value) }
                                })}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                              />
                            </div>
                            <div>
                              <Label className="mb-2 block text-sm font-medium text-gray-700">Image Shape</Label>
                              <select
                                className="w-full flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={resumeData.customization?.imageShape || 'circle'}
                                onChange={(e) => setResumeData({
                                  ...resumeData,
                                  customization: { ...resumeData.customization, imageShape: e.target.value }
                                })}
                              >
                                <option value="square">Square</option>
                                <option value="rounded">Rounded Corners</option>
                                <option value="circle">Circle</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Advanced Customization (Sidebar Colors only) */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAdvanced(!showAdvanced)}
                          className="w-full justify-between bg-white hover:bg-gray-50 text-gray-700 font-medium h-auto whitespace-normal text-left py-3"
                        >
                          <span>Advanced Customization (Sidebar Colors)</span>
                          {showAdvanced ? <Eye className="w-4 h-4 text-blue-600 shrink-0 ml-2" /> : <Plus className="w-4 h-4 shrink-0 ml-2" />}
                        </Button>

                        {showAdvanced && (
                          <div className="mt-4 space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                            {/* Sidebar Colors */}
                            {['template3', 'template9', 'template11', 'template12'].includes(String(resumeData.template_id || '').toLowerCase()) ? (
                              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                <Label className="mb-3 block text-base font-semibold text-gray-800">Sidebar Colors</Label>
                                <div className="space-y-3">
                                  {/* Sidebar Title */}
                                  <div className="flex flex-wrap gap-2 items-center justify-between p-3 rounded-md border border-gray-200 bg-gray-50/50 shadow-sm hover:border-blue-300 transition-colors">
                                    <label className="text-sm font-medium text-gray-700">Sidebar Title</label>
                                    <div className="flex items-center space-x-3 shrink-0">
                                      <div className="relative w-8 h-8 rounded-full shadow-sm ring-1 ring-black/10 overflow-hidden group hover:ring-blue-400 transition-all cursor-pointer">
                                        <input
                                          type="color"
                                          value={resumeData.colors?.sidebarTitleColor || '#1e40af'}
                                          onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, sidebarTitleColor: e.target.value } })}
                                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="w-full h-full" style={{ backgroundColor: resumeData.colors?.sidebarTitleColor || '#1e40af' }}></div>
                                      </div>
                                      <input
                                        type="text"
                                        value={resumeData.colors?.sidebarTitleColor || '#1e40af'}
                                        onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, sidebarTitleColor: e.target.value } })}
                                        className="w-20 h-8 px-2 text-xs font-mono border border-gray-200 rounded uppercase text-center focus:border-blue-500 focus:outline-none"
                                      />
                                    </div>
                                  </div>

                                  {/* Sidebar Text */}
                                  <div className="flex flex-wrap gap-2 items-center justify-between p-3 rounded-md border border-gray-200 bg-gray-50/50 shadow-sm hover:border-blue-300 transition-colors">
                                    <label className="text-sm font-medium text-gray-700">Sidebar Text</label>
                                    <div className="flex items-center space-x-3 shrink-0">
                                      <div className="relative w-8 h-8 rounded-full shadow-sm ring-1 ring-black/10 overflow-hidden group hover:ring-blue-400 transition-all cursor-pointer">
                                        <input
                                          type="color"
                                          value={resumeData.colors?.sidebarTextColor || '#333333'}
                                          onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, sidebarTextColor: e.target.value } })}
                                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="w-full h-full" style={{ backgroundColor: resumeData.colors?.sidebarTextColor || '#333333' }}></div>
                                      </div>
                                      <input
                                        type="text"
                                        value={resumeData.colors?.sidebarTextColor || '#333333'}
                                        onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, sidebarTextColor: e.target.value } })}
                                        className="w-20 h-8 px-2 text-xs font-mono border border-gray-200 rounded uppercase text-center focus:border-blue-500 focus:outline-none"
                                      />
                                    </div>
                                  </div>

                                  {/* Sidebar Background */}
                                  <div className="flex flex-wrap gap-2 items-center justify-between p-3 rounded-md border border-gray-200 bg-gray-50/50 shadow-sm hover:border-blue-300 transition-colors">
                                    <label className="text-sm font-medium text-gray-700">Sidebar Background</label>
                                    <div className="flex items-center space-x-3 shrink-0">
                                      <div className="relative w-8 h-8 rounded-full shadow-sm ring-1 ring-black/10 overflow-hidden group hover:ring-blue-400 transition-all cursor-pointer">
                                        <input
                                          type="color"
                                          value={resumeData.colors?.sidebarBackgroundColor || '#f3f4f6'}
                                          onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, sidebarBackgroundColor: e.target.value } })}
                                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="w-full h-full" style={{ backgroundColor: resumeData.colors?.sidebarBackgroundColor || '#f3f4f6' }}></div>
                                      </div>
                                      <input
                                        type="text"
                                        value={resumeData.colors?.sidebarBackgroundColor || '#f3f4f6'}
                                        onChange={(e) => setResumeData({ ...resumeData, colors: { ...resumeData.colors, sidebarBackgroundColor: e.target.value } })}
                                        className="w-20 h-8 px-2 text-xs font-mono border border-gray-200 rounded uppercase text-center focus:border-blue-500 focus:outline-none"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-gray-500 py-4 italic bg-gray-50 rounded-lg border border-gray-100">
                                Advanced colors are only available for Sidebar templates (Template 3, 9, 11, 12).
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <h3 className="text-xl font-bold mb-4 text-gray-800">Select Layout</h3>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template, index) => {
                      // Define unique colors and styles for each template
                      const templateStyles = [
                        { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'bg-blue-600', icon: 'text-blue-600' },
                        { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'bg-slate-700', icon: 'text-slate-700' },
                        { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'bg-purple-600', icon: 'text-purple-600' },
                        { bg: 'bg-green-50', border: 'border-green-200', accent: 'bg-green-600', icon: 'text-green-600' },
                        { bg: 'bg-indigo-50', border: 'border-indigo-200', accent: 'bg-indigo-600', icon: 'text-indigo-600' },
                        { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'bg-amber-600', icon: 'text-amber-600' },
                        { bg: 'bg-rose-50', border: 'border-rose-200', accent: 'bg-rose-600', icon: 'text-rose-600' },
                        { bg: 'bg-teal-50', border: 'border-teal-200', accent: 'bg-teal-600', icon: 'text-teal-600' },
                        { bg: 'bg-cyan-50', border: 'border-cyan-200', accent: 'bg-cyan-600', icon: 'text-cyan-600' },
                        { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'bg-orange-600', icon: 'text-orange-600' }
                      ];

                      const style = templateStyles[index % templateStyles.length];
                      const isSelected = resumeData.template_id === template.id;

                      return (
                        <Card
                          key={template.id}
                          className={`p-4 cursor-pointer transition-all hover:shadow-lg ${isSelected
                            ? 'border-2 border-blue-600 shadow-lg ring-2 ring-blue-200'
                            : 'border-2 border-gray-200 hover:border-blue-400'
                            }`}
                          onClick={() => {
                            setShowPreview(false)
                            setResumeData({ ...resumeData, template_id: template.id })
                            setTimeout(() => {
                              setShowPreview(true);
                            }, 100)
                          }}
                          data-testid={`template-${template.id}`}
                        >
                          {/* Template Preview */}
                          <div className={`aspect-[3/4] bg-gray-100 rounded-lg mb-3 border ${style.border} relative overflow-hidden group`}>
                            <img
                              src={`/assets/templates/${template.id}.png`}
                              alt={template.name}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://placehold.co/300x400?text=Preview+Coming+Soon";
                              }}
                            />

                            {/* Selected indicator */}
                            {isSelected && (
                              <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-1 z-10">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Template Info */}
                          <div className="text-center">
                            <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                            <p className="text-xs text-gray-600 capitalize">{template.category}</p>
                            {isSelected && (
                              <p className="text-xs text-blue-600 font-medium mt-2">✓ Selected</p>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div >
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-6 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Step {currentStep} of {steps.length}
                </span>
                <Button
                  onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                  disabled={currentStep === steps.length}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </Card >
          </div >

          {/* Preview Section */}
          {
            showPreview && (
              <div className={`
                lg:col-span-1 lg:block
                ${showPreview ? 'fixed inset-0 z-50 bg-gray-900/50 backdrop-blur-sm lg:static lg:bg-transparent lg:backdrop-blur-none lg:z-auto p-4 lg:p-0 flex items-center justify-center' : 'hidden'}
              `}>
                <Card className={`
                  relative lg:sticky lg:top-24 p-6 bg-white shadow-xl h-auto w-full max-w-4xl lg:max-w-none flex flex-col
                `}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Live Preview</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="lg:hidden"
                      onClick={() => setShowPreview(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div
                    className="border-2 rounded-lg overflow-hidden bg-white relative h-[80vh] lg:h-[800px] isolate"
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <PDFViewer
                      key={imageKey}
                      width="100%"
                      height="100%"
                      className="absolute inset-0"
                      showToolbar={canDownload}
                    >
                      <ResumeTemplate
                        isPaid={canDownload}
                        data={{
                          ...resumeData,
                          personal_info: {
                            ...resumeData.personal_info,
                            profile_picture: user?.picture
                              ? (user.picture.startsWith('/uploads')
                                ? `${BACKEND_URL}${user.picture}?t=${imageKey}`
                                : `${user.picture}?t=${imageKey}`)
                              : resumeData.personal_info.profile_picture
                          }
                        }} />
                    </PDFViewer>


                  </div>

                  {/* Download Overlay Button - Moved outside to prevent iframe click-jacking */}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canDownload) {
                        handleDownload();
                      } else {
                        // If it's a new resume (no ID) or unsaved, we save first then pay.
                        // Existing logic handles Update vs Create.
                        // passing 'true' to handleSave triggers the payment modal after save/create.
                        if (!id || id === 'new') {
                          handleSave(true);
                        } else {
                          setShowPayment(true);
                        }
                      }
                    }}
                    className="absolute bottom-10 right-10 z-50 shadow-xl bg-green-600 hover:bg-green-700 text-white rounded-full px-4 py-6 flex items-center gap-2 transition-transform hover:scale-105 cursor-pointer"
                    title={canDownload ? "Download PDF" : "Purchase to Download"}
                  >
                    <Download className="h-5 w-5" />
                    <span className="font-semibold text-base">{canDownload ? "Download PDF" : "Download PDF"}</span>
                  </Button>
                </Card>
              </div>
            )
          }
        </div >
      </div >

      {/* Payment Modal */}
      {
        showPayment && (
          <PaymentModal
            resumeId={id}
            onClose={() => setShowPayment(false)}
            onSuccess={handlePaymentSuccess}
          />
        )
      }
    </div >
  );
};

export default ResumeBuilder;
